import React, { useState, useEffect, useCallback, useRef } from 'react';
import { receiptScannerService, initReceiptScannerService } from '../../services/receiptScannerService';
import ReceiptScannerComponent from './ReceiptScannerComponent';
import ReceiptManagementComponent from './ReceiptManagementComponent';
import { useReceiptScanner } from '../../Contexts/ReceiptScannerContext';
import { mapFromReceiptScannerCategory } from '../../lib/categories';
import { guardAutoDetectedReceiptExpenseCategory } from '../../lib/receiptAutoCategoryGuard';
import { trackFeatureUsage } from '../../firebase';
import { deleteReceipt } from '../../services/receiptManagementService';

/**
 * Receipt Scanner Integration
 * 
 * This is the main component that integrates the receipt scanner functionality
 * into the 70Lives Business Finance Tracker. It handles authentication, component switching,
 * and communication with the parent application.
 * 
 * @param {Object} props
 * @param {Function} props.onAddTransaction - Callback when a new transaction is added
 * @param {Function} props.onUpdateTransaction - Callback when a transaction is updated
 * @param {string} props.userRole - User role for permission checks
 * @param {string} props.apiKey - Optional legacy API key
 * @param {Object} props.categories - Category list to use (income/expenses)
 * @param {Function} props.onAddCategory - Callback to create a new category
 */
function ReceiptScannerIntegration({
  onAddTransaction,
  onUpdateTransaction,
  userRole = 'user',
  apiKey = null,
  categories = null,
  transactionType = 'expenses',
  clients = [],
  paymentMethods = [],
  rctSubcontractors = [],
  supportsRct = false,
  supportsProjects = false,
  onAddCategory = null,
  onAddClient = null,
  onAddSupplier = null,
  activeWorkspace = null,
  initialDriveImportMode = null
}) {
  // Get context values
  const receiptContext = useReceiptScanner();
  
  // Use provided API key or fall back to context (optional legacy support)
  const effectiveApiKey = apiKey || receiptContext.apiKey;
  
  // State
  const [activeTab, setActiveTab] = useState('scanner');
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);
  const committedReceiptIdsRef = useRef(new Set());
  const cleanupInProgressRef = useRef(new Set());
  
  // Check if user has admin privileges
  const isAdmin = userRole === 'admin' || userRole === 'owner';
  const normalizedTransactionType = transactionType === 'income' ? 'income' : 'expenses';
  
  // Memoized error handling to prevent unnecessary re-renders
  const handleError = useCallback((message) => {
    console.error('[RECEIPT_SCANNER] Error:', message);
    setError(message);
    
    // Clear error after 5 seconds
    const timer = setTimeout(() => {
      setError(null);
    }, 5000);

    // Cleanup the timer if component unmounts
    return () => clearTimeout(timer);
  }, []);

  // Memoized connection test
  const testConnection = useCallback(async () => {
    try {
      await receiptScannerService.testConnection();
      console.log('[RECEIPT_SCANNER] Connection test successful');
    } catch (connectionError) {
      console.error('[RECEIPT_SCANNER] Connection test failed:', connectionError);
      handleError('Could not connect to the receipt scanner service. Please try again later.');
    }
  }, [handleError]);
  
  // Initialize the receipt scanner service
  useEffect(() => {
    let isMounted = true;

    const initializeService = async () => {
      try {
        if (effectiveApiKey) {
          console.log('[RECEIPT_SCANNER] Initializing receipt scanner service with optional legacy key');
          initReceiptScannerService(effectiveApiKey);
        } else {
          console.log('[RECEIPT_SCANNER] Initializing receipt scanner service in auth-token mode');
          initReceiptScannerService();
        }

        if (isMounted) {
          setIsInitialized(true);
          console.log('[RECEIPT_SCANNER] Service initialized successfully');

          // Test the connection
          await testConnection();
        }
      } catch (initError) {
        console.error('[RECEIPT_SCANNER] Failed to initialize receipt scanner service:', initError);
        if (isMounted) {
          setError('Failed to connect to receipt scanner service. Please try again.');
        }
      }
    };

    initializeService();

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [effectiveApiKey, testConnection]);
  
  const handleReceiptCommitted = useCallback((receiptId) => {
    const normalizedId = typeof receiptId === 'string' ? receiptId.trim() : '';
    if (!normalizedId) return;
    committedReceiptIdsRef.current.add(normalizedId);
  }, []);

  const handleReceiptAbandoned = useCallback(async (receiptId) => {
    const normalizedId = typeof receiptId === 'string' ? receiptId.trim() : '';
    if (!normalizedId || normalizedId.startsWith('receipt-')) {
      return;
    }

    if (committedReceiptIdsRef.current.has(normalizedId)) {
      return;
    }

    if (cleanupInProgressRef.current.has(normalizedId)) {
      return;
    }

    cleanupInProgressRef.current.add(normalizedId);

    try {
      await deleteReceipt(normalizedId, { workspace: activeWorkspace });
      console.log('[RECEIPT_SCANNER] Deleted abandoned scanned receipt:', normalizedId);
    } catch (cleanupError) {
      console.warn('[RECEIPT_SCANNER] Failed to delete abandoned receipt:', normalizedId, cleanupError);
      cleanupInProgressRef.current.delete(normalizedId);
    }
  }, [activeWorkspace]);

  // Memoized notification and transaction handlers
  const handleReceiptProcessed = useCallback(async (transaction) => {
    console.log('[RECEIPT_SCANNER] Original receipt data:', transaction);
    
    const isIncomeTransaction = normalizedTransactionType === 'income' || transaction.type === 'income';
    const mappedCategory = isIncomeTransaction
      ? (transaction.category || categories?.income?.[0]?.id || 'nail_services')
      : mapFromReceiptScannerCategory(transaction.category || 'supplies');
    const category = isIncomeTransaction
      ? mappedCategory
      : guardAutoDetectedReceiptExpenseCategory({
          category: mappedCategory,
          amount: transaction.amount,
          vendor: transaction.vendor || transaction.description || '',
          items: transaction.items || transaction.lineItems || [],
          rawText: transaction.rawText || transaction.text || transaction.fullText || ''
        }) || mappedCategory;
    
    const rawVatRate = transaction.vatRate;
    const parsedVatRate =
      rawVatRate === undefined || rawVatRate === null || rawVatRate === ''
        ? null
        : parseFloat(rawVatRate);
    const vatRate = Number.isFinite(parsedVatRate) ? parsedVatRate : null;

    const rawVatAmount = transaction.vatAmount;
    const parsedVatAmount =
      rawVatAmount === undefined || rawVatAmount === null || rawVatAmount === ''
        ? null
        : parseFloat(rawVatAmount);
    const vatAmount = Number.isFinite(parsedVatAmount) ? parsedVatAmount : null;
    
    const sourceReceiptId = transaction.receiptId || transaction.id || null;

    const rawTransactionId = transaction.id || transaction.transactionId || transaction.timestamp;
    const transactionId = rawTransactionId
      ? String(rawTransactionId)
      : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    // Create a transaction object preserving original date
    const newTransaction = {
      id: transactionId,
      type: isIncomeTransaction ? 'income' : 'expenses',
      category: category,
      description: transaction.vendor || transaction.description || 'Receipt scan',
      amount: parseFloat(transaction.amount) || 0,
      // Preserve original date from receipt
      date: transaction.date || new Date().toISOString().split('T')[0],
      paymentMethod: transaction.paymentMethod || 'card',
      paymentStatus: transaction.paymentStatus,
      paymentBreakdown: Array.isArray(transaction.paymentBreakdown) ? transaction.paymentBreakdown : [],
      amountPaid: transaction.amountPaid,
      outstandingAmount: transaction.outstandingAmount,
      saleTotalAmount: transaction.saleTotalAmount,
      grossAmount: transaction.grossAmount,
      invoiceDate: transaction.invoiceDate,
      paymentDate: transaction.paymentDate,
      isPartPaymentSale: !!transaction.isPartPaymentSale,
      isSplitPayment: !!transaction.isSplitPayment,
      clientId: transaction.clientId || '',
      clientName: transaction.clientName || '',
      clientAllocations: Array.isArray(transaction.clientAllocations) ? transaction.clientAllocations : [],
      supplierId: transaction.supplierId || '',
      supplierName: transaction.supplierName || transaction.vendor || '',
      jobReference: transaction.jobReference || '',
      allocateToClient: !!transaction.allocateToClient,
      billableToClient: !!transaction.billableToClient,
      vatRate: vatRate,
      vatAmount: vatAmount,
      currency: transaction.currency || '',
      notes: transaction.notes || '',
      items: transaction.items || transaction.lineItems || [],
      categoryBreakdown: Array.isArray(transaction.categoryBreakdown)
        ? transaction.categoryBreakdown.map((entry) => ({
            ...entry,
            category: mapFromReceiptScannerCategory(entry?.category || category)
          }))
        : [],
      isSplitTransaction: !!transaction.isSplitTransaction,
      // Include unique receipt reference
      receiptId: sourceReceiptId || undefined,
      receiptImageUrl: transaction.receiptImageUrl || transaction.imageUrl,
      keepReceiptScannerOpen: !!transaction.keepReceiptScannerOpen
    };
    
    console.log('[RECEIPT_SCANNER] Formatted transaction with preserved date:', newTransaction);
    
    if (typeof onAddTransaction === 'function') {
      console.log('[RECEIPT_SCANNER] Calling onAddTransaction callback');
      await onAddTransaction(newTransaction);
    } else {
      console.error('[RECEIPT_SCANNER] onAddTransaction callback is not a function!');
      handleError('Unable to add transaction. Application integration error.');
      return;
    }
    
    // Show notification
    trackFeatureUsage('receipt_scanner');
    setNotification({
      type: 'success',
      message: 'Receipt processed and added to transactions.'
    });
    
    // Clear notification after 3 seconds
    const timer = setTimeout(() => {
      setNotification(null);
    }, 3000);
  
    return () => clearTimeout(timer);
  }, [onAddTransaction, handleError, normalizedTransactionType, categories]);
  
  const handleReceiptUpdated = useCallback((receipt) => {
    console.log('[RECEIPT_SCANNER] Receipt update requested, original data:', receipt);
    
    // Get the category from the receipt
    const mappedCategory = mapFromReceiptScannerCategory(receipt.category || 'supplies');
    const category = guardAutoDetectedReceiptExpenseCategory({
      category: mappedCategory,
      amount: receipt.amount,
      vendor: receipt.vendor || receipt.description || '',
      items: receipt.items || receipt.lineItems || [],
      rawText: receipt.rawText || receipt.text || receipt.fullText || ''
    }) || mappedCategory;
    
    const rawVatRate = receipt.vatRate;
    const parsedVatRate =
      rawVatRate === undefined || rawVatRate === null || rawVatRate === ''
        ? null
        : parseFloat(rawVatRate);
    const vatRate = Number.isFinite(parsedVatRate) ? parsedVatRate : null;

    const rawVatAmount = receipt.vatAmount;
    const parsedVatAmount =
      rawVatAmount === undefined || rawVatAmount === null || rawVatAmount === ''
        ? null
        : parseFloat(rawVatAmount);
    const vatAmount = Number.isFinite(parsedVatAmount) ? parsedVatAmount : null;
    
    // Format the transaction update properly
    const updatedTransaction = {
      id: receipt.id || Date.now(),
      type: 'expenses', // Ensure transaction type is set
      category: category,
      description: receipt.vendor || receipt.description || 'Receipt scan',
      amount: parseFloat(receipt.amount) || 0,
      date: receipt.date || new Date().toISOString().split('T')[0],
      paymentMethod: receipt.paymentMethod || 'card',
      vatRate: vatRate,
      vatAmount: vatAmount,
      currency: receipt.currency || '',
      notes: receipt.notes || '',
      items: receipt.items || receipt.lineItems || [],
      // Important: Ensure receipt references are included
      receiptId: receipt.receiptId || receipt.id,
      receiptImageUrl: receipt.receiptImageUrl || receipt.imageUrl
    };
    
    console.log('[RECEIPT_SCANNER] Formatted transaction update:', updatedTransaction);
    
    // Verify that the callback exists
    if (typeof onUpdateTransaction === 'function') {
      console.log('[RECEIPT_SCANNER] Calling onUpdateTransaction callback');
      onUpdateTransaction(updatedTransaction);
    } else {
      console.error('[RECEIPT_SCANNER] onUpdateTransaction callback is not a function!', {
        callback: onUpdateTransaction,
        type: typeof onUpdateTransaction
      });
      handleError('Unable to update transaction. Application integration error.');
      return;
    }
    
    // Show notification
    setNotification({
      type: 'success',
      message: 'Receipt updated successfully.'
    });
    
    // Clear notification after 3 seconds
    const timer = setTimeout(() => {
      setNotification(null);
    }, 3000);

    // Cleanup the timer if component unmounts
    return () => clearTimeout(timer);
  }, [onUpdateTransaction, handleError]);
  
  // If not initialized, show error or loading
  if (!isInitialized) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Receipt Scanner</h2>
        
        {error ? (
          <div className="bg-red-50 p-4 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Connection Error
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{error}</p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex justify-center items-center h-32">
            <svg className="animate-spin h-8 w-8 text-indigo-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="ml-3 text-gray-600">Connecting to receipt scanner service...</span>
          </div>
        )}
      </div>
    );
  }
  
  return (
    <div className="bg-white rounded-lg shadow">
      {/* Tab Navigation */}
      <div className="border-b">
        <nav className="flex -mb-px">
          <button
            className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
              activeTab === 'scanner'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            onClick={() => setActiveTab('scanner')}
          >
            Receipt Scanner
          </button>
          
          {isAdmin && (
            <button
              className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                activeTab === 'management'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              onClick={() => setActiveTab('management')}
            >
              Receipt Management
            </button>
          )}
        </nav>
      </div>
      
      {/* Notification */}
      {notification && (
        <div className={`p-4 ${notification.type === 'success' ? 'bg-green-50' : 'bg-yellow-50'}`}>
          <div className="flex">
            <div className="flex-shrink-0">
              {notification.type === 'success' ? (
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <div className="ml-3">
              <p className={`text-sm font-medium ${notification.type === 'success' ? 'text-green-800' : 'text-yellow-800'}`}>
                {notification.message}
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800">
                {error}
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Hidden Debug Info */}
      <div style={{ display: 'none' }} className="p-2 bg-gray-100 text-xs">
        <p><strong>Debug:</strong> Receipt Scanner Integration is active</p>
        <p><strong>Auth Mode:</strong> {effectiveApiKey ? 'Legacy key + token' : 'Firebase token'}</p>
        <p><strong>User Role:</strong> {userRole}</p>
        <p><strong>Callbacks:</strong> {onAddTransaction ? 'onAdd ✓' : 'onAdd ✗'} | {onUpdateTransaction ? 'onUpdate ✓' : 'onUpdate ✗'}</p>
      </div>
      
      {/* Content Area */}
      <div>
        {activeTab === 'scanner' && (
          <ReceiptScannerComponent
            onReceiptProcessed={handleReceiptProcessed}
            onError={handleError}
            userRole={userRole}
            categories={categories}
            transactionType={normalizedTransactionType}
            clients={clients}
            paymentMethods={paymentMethods}
            rctSubcontractors={rctSubcontractors}
            supportsRct={supportsRct}
            supportsProjects={supportsProjects}
            onAddCategory={onAddCategory}
            onAddClient={onAddClient}
            onAddSupplier={onAddSupplier}
            onReceiptAbandoned={handleReceiptAbandoned}
            onReceiptCommitted={handleReceiptCommitted}
            activeWorkspace={activeWorkspace}
            initialDriveImportMode={initialDriveImportMode}
          />
        )}
        
        {activeTab === 'management' && isAdmin && (
          <ReceiptManagementComponent
            onReceiptUpdated={handleReceiptUpdated}
            onError={handleError}
          />
        )}
      </div>
    </div>
  );
}

export default ReceiptScannerIntegration;
